import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/* Monday-based week start for a given date, offset by N weeks back */
function getWeekRange(weeksBack) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayOfWeek = today.getDay(); // 0=Sun
  const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const thisMonday = new Date(today);
  thisMonday.setDate(today.getDate() - mondayOffset - weeksBack * 7);

  const start = new Date(thisMonday);
  const end = new Date(thisMonday);
  end.setDate(end.getDate() + 7); // exclusive end (next Monday)
  return { start, end };
}

function shortDay(date) {
  return DAY_NAMES[date.getDay() === 0 ? 6 : date.getDay() - 1];
}

function fmtTime(m) {
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${m}m`;
}

function Trend({ current, previous, label }) {
  const diff = current - previous;
  if (diff === 0) return <span className="stat-sub">Same as last week</span>;
  const up = diff > 0;
  return (
    <span className="stat-sub" style={{ color: up ? '#dc2626' : '#16a34a' }}>
      {up ? '\u2191' : '\u2193'} {label || fmtTime(Math.abs(diff))} vs last week
    </span>
  );
}

export default function Reports({ kidId, parentId, plan, features, deviceFilter }) {
  const [week, setWeek] = useState(0); // 0 = this week, 1 = last week
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [daily, setDaily] = useState([]);
  const [sites, setSites] = useState([]);
  const [stats, setStats] = useState({
    totalMins: 0, prevMins: 0, topApp: '-', siteCount: 0, alertCount: 0,
  });

  const load = useCallback(async () => {
    if (!parentId) return;
    setLoading(true);
    setProgress(10);

    const curr = getWeekRange(week);
    const prev = getWeekRange(week + 1);

    // Helper — applies parent + optional kid scoping to a query.
    const scope = (q) => {
      q = q.eq('parent_id', parentId);
      if (kidId) q = q.eq('kid_id', kidId);
      if (deviceFilter && deviceFilter !== 'all') q = q.eq('source', deviceFilter);
      return q;
    };

    const [
      { data: usageRaw },
      { data: sitesRaw },
      { count: alertCount },
      { data: prevUsage },
    ] = await Promise.all([
      scope(supabase.from('app_usage').select('app_name,duration_seconds,timestamp'))
        .gte('timestamp', curr.start.toISOString())
        .lt('timestamp', curr.end.toISOString()),
      scope(supabase.from('websites').select('url,title,flagged,timestamp'))
        .gte('timestamp', curr.start.toISOString())
        .lt('timestamp', curr.end.toISOString()),
      scope(supabase.from('alerts').select('*', { count: 'exact', head: true }))
        .gte('timestamp', curr.start.toISOString())
        .lt('timestamp', curr.end.toISOString()),
      scope(supabase.from('app_usage').select('duration_seconds'))
        .gte('timestamp', prev.start.toISOString())
        .lt('timestamp', prev.end.toISOString()),
    ]);
    setProgress(60);

    const usage = usageRaw || [];

    // Daily chart data (Mon-Sun)
    const dayBuckets = {};
    for (let i = 0; i < 7; i++) {
      const d = new Date(curr.start);
      d.setDate(d.getDate() + i);
      dayBuckets[d.toDateString()] = { day: shortDay(d), minutes: 0 };
    }
    usage.forEach(r => {
      const key = new Date(r.timestamp).toDateString();
      if (dayBuckets[key]) dayBuckets[key].minutes += (r.duration_seconds || 0);
    });
    setDaily(Object.values(dayBuckets).map(b => ({
      day: b.day,
      minutes: Math.round(b.minutes / 60),
    })));

    // Top app
    const appTotals = {};
    usage.forEach(r => {
      appTotals[r.app_name] = (appTotals[r.app_name] || 0) + r.duration_seconds;
    });
    const topApp = Object.entries(appTotals).sort((a, b) => b[1] - a[1])[0];

    // Top 10 sites
    const siteMap = {};
    (sitesRaw || []).forEach(r => {
      const key = r.title || r.url;
      if (!siteMap[key]) siteMap[key] = { title: key, count: 0, flagged: r.flagged };
      siteMap[key].count++;
    });
    setSites(Object.values(siteMap).sort((a, b) => b.count - a.count).slice(0, 10));

    // Stats
    const totalSecs = usage.reduce((s, r) => s + r.duration_seconds, 0);
    const prevSecs = (prevUsage || []).reduce((s, r) => s + r.duration_seconds, 0);
    setStats({
      totalMins: Math.round(totalSecs / 60),
      prevMins: Math.round(prevSecs / 60),
      topApp: topApp ? topApp[0] : '-',
      siteCount: (sitesRaw || []).length,
      alertCount: alertCount || 0,
    });

    setProgress(100);
    setLoading(false);
  }, [week, parentId, kidId, deviceFilter]);

  useEffect(() => { load(); }, [load]);

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify({
      generated: new Date().toISOString(),
      week: week === 0 ? 'This week' : 'Last week',
      stats, daily, sites,
    }, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = `ParentsProtection_Report_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Weekly Report</h1>
        <div className="filter-bar" style={{ marginBottom: 0 }}>
          <button
            className={`filter-btn${week === 0 ? ' active' : ''}`}
            onClick={() => setWeek(0)}
          >
            This Week
          </button>
          <button
            className={`filter-btn${week === 1 ? ' active' : ''}`}
            onClick={() => setWeek(1)}
          >
            Last Week
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-icon">{'\uD83D\uDCCA'}</div>
          <div className="loading-title">Loading report...</div>
          <div className="loading-bar-track">
            <div className="loading-bar-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="loading-percent">{progress}%</div>
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="stat-row">
            <div className="stat-card">
              <div className="stat-label">Total Screen Time</div>
              <div className="stat-value">{fmtTime(stats.totalMins)}</div>
              <Trend current={stats.totalMins} previous={stats.prevMins} />
            </div>
            <div className="stat-card">
              <div className="stat-label">Top App</div>
              <div className="stat-value" style={{ fontSize: 20 }}>{stats.topApp}</div>
              <span className="stat-sub">Most used this week</span>
            </div>
            <div className="stat-card">
              <div className="stat-label">Sites Visited</div>
              <div className="stat-value">{stats.siteCount}</div>
              <span className="stat-sub">Unique page visits</span>
            </div>
            <div className="stat-card">
              <div className="stat-label">Alerts</div>
              <div className="stat-value" style={{ color: stats.alertCount > 0 ? '#dc2626' : '#16a34a' }}>
                {stats.alertCount}
              </div>
              <span className="stat-sub">Triggered this week</span>
            </div>
          </div>

          {/* Daily bar chart */}
          <div className="card">
            <div className="card-title">Daily Screen Time</div>
            {daily.every(d => d.minutes === 0) ? (
              <div className="empty-state">
                <div className="desc">No screen time data for this week.</div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={daily} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip
                    formatter={(v) => [`${v} min`, 'Screen Time']}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  />
                  <Bar dataKey="minutes" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Top 10 websites */}
          <div className="card">
            <div className="card-title">Top 10 Websites</div>
            {sites.length === 0 ? (
              <div className="empty-state">
                <div className="desc">No websites tracked this week.</div>
              </div>
            ) : (
              <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Site</th>
                    <th style={{ width: 80 }}>Visits</th>
                    <th style={{ width: 100 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sites.map((s, i) => (
                    <tr key={i} className={s.flagged ? 'flagged' : ''}>
                      <td style={{ color: 'var(--muted)', fontWeight: 700 }}>{i + 1}</td>
                      <td style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.title}
                      </td>
                      <td>{s.count}</td>
                      <td>
                        <span className={`badge ${s.flagged ? 'badge-red' : 'badge-green'}`}>
                          {s.flagged ? 'Flagged' : 'Safe'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>

          {/* Export */}
          <button className="btn" onClick={exportJSON} style={{ marginTop: 8 }}>
            Export JSON
          </button>
        </>
      )}
    </div>
  );
}
